// @ts-nocheck
import { forwardRef } from 'react';
import {
  Box,
  Group,
  Stack,
  Text,
  ActionIcon,
  ThemeIcon,
  useMantineTheme,
  useComputedColorScheme,
} from '@mantine/core';
import {
  IconX,
  IconInfoCircle,
  IconAlertTriangle,
  IconAlertCircle,
  IconCircleCheck,
} from '@tabler/icons-react';
import { Paper } from '../../../ui';
import { FadeIn } from '../../../animations';
import type { AlertCardProps } from '../types';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

/**
 * AlertCard - Single alert display
 *
 * Displays a single alert with severity-based styling and optional dismiss action.
 */
const AlertCard = forwardRef<HTMLDivElement, AlertCardProps>(
  (
    {
      alert,
      onDismiss,
      variant = 'default',
      showTimestamp = true,
      className,
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const colorScheme = useComputedColorScheme('light');
    const isDark = colorScheme === 'dark';
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const severityConfig = {
      info: {
        color: 'blue',
        icon: IconInfoCircle,
        bgColor: isDark ? theme.colors.blue[9] : theme.colors.blue[0],
      },
      warning: {
        color: 'yellow',
        icon: IconAlertTriangle,
        bgColor: isDark ? theme.colors.yellow[9] : theme.colors.yellow[0],
      },
      error: {
        color: 'red',
        icon: IconAlertCircle,
        bgColor: isDark ? theme.colors.red[9] : theme.colors.red[0],
      },
      success: {
        color: 'green',
        icon: IconCircleCheck,
        bgColor: isDark ? theme.colors.green[9] : theme.colors.green[0],
      },
    };

    const config = severityConfig[alert.severity];
    const Icon = config.icon;

    const getRelativeTime = (date: Date) => {
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      return `${diffDays}d ago`;
    };

    const isCompact = variant === 'compact';
    const isDetailed = variant === 'detailed';

    return (
      <FadeIn>
        <Paper
          ref={ref}
          withBorder
          p={isCompact ? 'sm' : 'md'}
          className={className}
          style={{
            backgroundColor: variant === 'default' ? config.bgColor : undefined,
            borderLeft: `4px solid ${theme.colors[config.color][6]}`,
          }}
        >
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Group align="flex-start" gap="sm" style={{ flex: 1 }}>
              <ThemeIcon
                color={config.color}
                variant="light"
                size={isCompact ? 'md' : 'lg'}
                radius="md"
              >
                <Icon size={isCompact ? 16 : 20} />
              </ThemeIcon>

              <Stack gap={isCompact ? 4 : 8} style={{ flex: 1 }}>
                <Group justify="space-between" align="flex-start">
                  <Text size={isCompact ? 'sm' : 'md'} fw={600} style={accessibilityStyles}>
                    {alert.title}
                  </Text>

                  {showTimestamp && (
                    <Text size="xs" c="dimmed" style={accessibilityStyles}>
                      {getRelativeTime(alert.timestamp)}
                    </Text>
                  )}
                </Group>

                <Text size={isCompact ? 'xs' : 'sm'} c="dimmed" style={accessibilityStyles}>
                  {alert.message}
                </Text>

                {isDetailed && alert.category && (
                  <Text size="xs" c="dimmed" tt="uppercase" fw={500} style={accessibilityStyles}>
                    {alert.category}
                  </Text>
                )}

                {alert.action && (
                  <Box>
                    <ActionIcon
                      variant="light"
                      color={config.color}
                      onClick={alert.action.onClick}
                    >
                      {alert.action.icon}
                    </ActionIcon>
                  </Box>
                )}
              </Stack>
            </Group>

            {alert.dismissible !== false && onDismiss && (
              <ActionIcon
                variant="subtle"
                color="gray"
                onClick={() => onDismiss(alert.id)}
                size="sm"
              >
                <IconX size={14} />
              </ActionIcon>
            )}
          </Group>
        </Paper>
      </FadeIn>
    );
  }
);

AlertCard.displayName = 'AlertCard';

export default AlertCard;
